import { useEffect, useCallback } from "react";

interface KeyboardShortcutHandlers {
  onSearch?: () => void;
  onNewTab?: () => void;
  onNextTab?: () => void;
  onPrevTab?: () => void;
  onToggleLinkMode?: () => void;
}

/**
 * Hook that handles keyboard shortcuts for the advanced search component.
 * 
 * Shortcuts:
 * - Ctrl+Enter: Trigger search
 * - Ctrl+N: Create new tab
 * - Ctrl+→: Next tab
 * - Ctrl+←: Previous tab
 * - Ctrl+L: Toggle quick-link mode (future feature)
 */
export function useKeyboardShortcuts({
  onSearch,
  onNewTab,
  onNextTab,
  onPrevTab,
  onToggleLinkMode,
}: KeyboardShortcutHandlers) {
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    // Only handle if Ctrl/Cmd is pressed
    const isCtrl = e.ctrlKey || e.metaKey;
    
    if (!isCtrl) return;

    switch (e.key) {
      case "Enter":
        if (onSearch) {
          e.preventDefault();
          onSearch();
        }
        break;
        
      case "n":
      case "N":
        if (onNewTab) {
          e.preventDefault();
          onNewTab();
        }
        break;
        
      case "ArrowRight":
        if (onNextTab) {
          e.preventDefault();
          onNextTab();
        }
        break;
        
      case "ArrowLeft":
        if (onPrevTab) {
          e.preventDefault();
          onPrevTab();
        }
        break;
        
      case "l":
      case "L":
        if (onToggleLinkMode) {
          e.preventDefault();
          onToggleLinkMode();
        }
        break;
    }
  }, [onSearch, onNewTab, onNextTab, onPrevTab, onToggleLinkMode]);

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleKeyDown]);
}
